import { PropsWithChildren, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Navigate, RouteObject, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import yn from "yn";
import AppHeader, { HEADER_HEIGHT } from "~/components/AppHeader";
import Loading from "~/components/Loading";
import useAppHeader from "~/contexts/header";
import { useAtlasRedesignFeature } from "~/hooks/useAtlasRedesignFeature";
import {
  Admin,
  Cohorts,
  DataBrowser,
  DataOverview,
  Home,
  NotFound,
  ResearchWorkbench,
  Shared,
  SmPortal,
  ShadcnTest,
} from "~/pages";
import { V2Placeholder } from "~/pages/v2/V2Placeholder";
import { ProtectedAppRoute } from "./components/ProtectedAppRoute";
import redirects from "./redirects";
import { ROUTE_404 } from "./router/constants";
import { VIEW_SM_APP } from "./router/permissions";

interface AppLayoutProps extends PropsWithChildren {
  enabled?: boolean;
}

const {
  VITE_RWB_ENABLED,
  VITE_SM_ENABLED,
  VITE_OUTREACH_API,
  VITE_PARTICIPANT_API,
  VITE_SURVEY_API,
  VITE_DATA_DASHBOARD_URL,
  VITE_ADMIN_APP_ENABLED,
  VITE_COHORT_EXPLORER_ENABLED,
} = import.meta.env;

/**
 * Top-level app layout
 * @param enabled - Build-time, env specific feature flag support
 * @param children
 * @constructor
 */
const AppLayout = ({ enabled = true, children }: AppLayoutProps) => {
  const location = useLocation();
  const wrapperRef = useRef<HTMLElement>(null);
  const { setCenterContent } = useAppHeader();
  const contentHeight = `calc(100vh - ${HEADER_HEIGHT})`;

  useEffect(() => {
    // Handle resetting scroll on navigation
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [wrapperRef.current, location.pathname]);

  useLayoutEffect(() => {
    // Ugly hack to avoid flicker between tab switches on participant profile
    // TODO: Deprecate in favor of a more generic solution for multi-page center content
    if (!/^\/sm\/participants\/\d+.*$/.test(location.pathname)) {
      // Reset page specific center content when navigating
      setCenterContent(null);
    }
  }, [location.pathname, setCenterContent]);

  if (!enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
      <AppHeader />
      <Box
        component="main"
        ref={wrapperRef}
        sx={{
          position: "relative",
          height: contentHeight,
          overflow: "auto",
          bgcolor: "common.pageBackground",
        }}
      >
        {children}
      </Box>
    </QueryParamProvider>
  );
};

/**
 * Wrapper for lazy imported apps
 * @param children - Route content which might also include its own sub-router
 * @constructor
 */
const LazyWrapper = ({ children }: PropsWithChildren) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

const AtlasLayout = ({ children, enabled = true }: AppLayoutProps) => {
  const atlasRedesignEnabled = useAtlasRedesignFeature();
  return <AppLayout enabled={atlasRedesignEnabled && enabled}>{children}</AppLayout>;
};

const routes: RouteObject[] = [
  {
    index: true,
    element: (
      <AppLayout>
        <Home />
      </AppLayout>
    ),
  },
  ...redirects,
  {
    path: "shadcn-test",
    element: (
      <AppLayout enabled={import.meta.env.DEV}>
        <ShadcnTest />
      </AppLayout>
    ),
  },
  // Note: Global files browser is now under /data-browser/files
  // The redirect from /files to /data-browser/files is in redirects.tsx
  {
    path: "cohorts/*",
    element: (
      <AppLayout enabled={yn(VITE_COHORT_EXPLORER_ENABLED, { default: false })}>
        <LazyWrapper>
          <ProtectedAppRoute permission="view:cohort-explorer-app">
            <Cohorts />
          </ProtectedAppRoute>
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "workbenches/*",
    element: (
      <AppLayout enabled={yn(VITE_RWB_ENABLED, { default: false })}>
        <LazyWrapper>
          <ResearchWorkbench />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "data-browser/*",
    element: (
      <AppLayout>
        <DataBrowser />
      </AppLayout>
    ),
  },
  {
    path: "data-overview",
    element: (
      <AppLayout enabled={!!VITE_DATA_DASHBOARD_URL}>
        <DataOverview />
      </AppLayout>
    ),
  },
  {
    path: "sm/*",
    element: (
      <AppLayout enabled={yn(VITE_SM_ENABLED, { default: false })}>
        <LazyWrapper>
          <ProtectedAppRoute permission={VIEW_SM_APP}>
            <SmPortal />
          </ProtectedAppRoute>
        </LazyWrapper>
      </AppLayout>
    ),
    handle: {
      serverHealth: [
        {
          endpoint: VITE_OUTREACH_API,
          label: "Outreach Service",
        },
        {
          endpoint: VITE_PARTICIPANT_API,
          label: "Participant Service",
        },
        {
          endpoint: VITE_SURVEY_API,
          label: "Survey Service",
        },
      ],
    },
  },
  {
    path: "admin/*",
    element: (
      <AppLayout enabled={yn(VITE_ADMIN_APP_ENABLED, { default: false })}>
        <LazyWrapper>
          <Admin />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "shared/*",
    element: (
      <AppLayout>
        <LazyWrapper>
          <Shared />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "v2",
    children: [
      {
        index: true,
        element: (
          <AtlasLayout>
            <V2Placeholder page="Home" />
          </AtlasLayout>
        ),
      },
      {
        path: "cohorts/*",
        element: (
          <AtlasLayout enabled={yn(VITE_COHORT_EXPLORER_ENABLED, { default: false })}>
            <LazyWrapper>
              <ProtectedAppRoute permission="view:cohort-explorer-app">
                <V2Placeholder page="Cohorts" />
              </ProtectedAppRoute>
            </LazyWrapper>
          </AtlasLayout>
        ),
      },
      {
        path: "workbenches/*",
        element: (
          <AtlasLayout enabled={yn(VITE_RWB_ENABLED, { default: false })}>
            <LazyWrapper>
              <V2Placeholder page="Research Workbench" />
            </LazyWrapper>
          </AtlasLayout>
        ),
      },
      {
        path: "data-browser/*",
        element: (
          <AtlasLayout>
            <V2Placeholder page="Data Browser" />
          </AtlasLayout>
        ),
      },
      {
        path: "data-overview",
        element: (
          <AtlasLayout enabled={!!VITE_DATA_DASHBOARD_URL}>
            <V2Placeholder page="Data Overview" />
          </AtlasLayout>
        ),
      },
      {
        path: "sm/*",
        element: (
          <AtlasLayout enabled={yn(VITE_SM_ENABLED, { default: false })}>
            <LazyWrapper>
              <ProtectedAppRoute permission={VIEW_SM_APP}>
                <V2Placeholder page="Study Management" />
              </ProtectedAppRoute>
            </LazyWrapper>
          </AtlasLayout>
        ),
      },
      {
        path: "admin/*",
        element: (
          <AtlasLayout enabled={yn(VITE_ADMIN_APP_ENABLED, { default: false })}>
            <LazyWrapper>
              <V2Placeholder page="Admin" />
            </LazyWrapper>
          </AtlasLayout>
        ),
      },
      {
        path: "shared/*",
        element: (
          <AtlasLayout>
            <LazyWrapper>
              <V2Placeholder page="Shared" />
            </LazyWrapper>
          </AtlasLayout>
        ),
      },
    ],
  },
  { path: "404", element: <NotFound /> },
  { path: "*", element: <Navigate to={ROUTE_404} replace /> },
];

export default routes;
